import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';

/**
 * The strongest tint a matrix cell may take, per kind, in percent of its fill
 * (`--accent` for direct, `--warn` for halo) over the panel. The cell's 9px
 * figures are `--ink-1`, and past these values they fall below WCAG AA (4.5:1)
 * in one theme or the other: at the old 68% a dark-theme halo cell read 2.5:1
 * (crawl backlog B11). Switching to a light-on-dark ink does not help, because
 * the mid-range tints fail both inks. `perf-halo-contrast.test.ts` recomputes
 * the ratio at each cap from the theme tokens.
 */
export const MATRIX_CELL_MAX_INTENSITY = { direct: 48, halo: 40 } as const;
const MATRIX_CELL_MIN_INTENSITY = 4;

/** A cell's tint for a magnitude in [0, 1], from the floor up to its kind's cap. */
export function matrixCellIntensity(kind: keyof typeof MATRIX_CELL_MAX_INTENSITY, magnitude: number): number {
  const max = MATRIX_CELL_MAX_INTENSITY[kind];
  return MATRIX_CELL_MIN_INTENSITY + Math.min(1, Math.max(0, magnitude)) * (max - MATRIX_CELL_MIN_INTENSITY);
}

import type { ReportQueryResponse } from '@/api/types';
import { Dropdown } from '@/components/dropdowns';
import { useAttachedElement } from '@/components/virtual-table';
import {
  perfHaloMetricValue,
  projectPerfHaloMetrics,
} from '@/lib/perf-halo-metrics';
import type { PerfHaloMetrics } from '@/lib/perf-halo-metrics';
import {
  reportMetricIsAvailable,
  reportingMetricAvailability,
} from '@/lib/reporting-availability';

import './perf-halo-analysis.css';

export type PerfHaloAnalysisMetric = 'tickets' | 'ticket_value';

interface ProductionIdentity {
  id: string;
  name: string;
}

interface HaloFlow {
  key: string;
  source: ProductionIdentity;
  destination: ProductionIdentity;
  audienceType: string;
  kind: 'direct' | 'halo';
  ticketsMicros: number;
  valueMicros: number;
}

interface SankeyFlow extends HaloFlow {
  sourceCount: number;
  destinationCount: number;
}

interface TooltipState {
  title: string;
  lines: Array<{ label: string; value: string }>;
  x: number;
  y: number;
}

interface NodeGeometry {
  id: string;
  name: string;
  x: number;
  y: number;
  height: number;
  value: number;
}

interface RibbonGeometry {
  flow: SankeyFlow;
  path: string;
  sourceOffset: number;
  sourceHeight: number;
  destinationOffset: number;
  destinationHeight: number;
}

interface AudienceSummary {
  type: string;
  metrics: PerfHaloMetrics;
  attributionAvailable: boolean;
  deliveryAvailable: boolean;
}

const OTHER_SOURCE_ID = '__other_source_productions__';
const OTHER_DESTINATION_ID = '__other_destination_productions__';
const MAX_VISIBLE_NODES = 14;
const SANKEY_HEIGHT = 520;
const SANKEY_NODE_WIDTH = 12;
const SANKEY_NODE_GAP = 5;
const SANKEY_PADDING = { top: 12, right: 190, bottom: 12, left: 190 };

const HALO_METRIC_OPTIONS = [
  { id: 'ticket_value', label: 'Ticket Value' },
  { id: 'tickets', label: 'Tickets' },
];

function finite(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatTicketsMicros(value: number): string {
  return (value / 1_000_000).toLocaleString('en-US', {
    maximumFractionDigits: 6,
  });
}

function formatValueMicros(value: number): string {
  return (value / 1_000_000).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMetricMicros(flow: Pick<HaloFlow, 'ticketsMicros' | 'valueMicros'>, metric: PerfHaloAnalysisMetric): string {
  return metric === 'tickets'
    ? formatTicketsMicros(flow.ticketsMicros)
    : formatValueMicros(flow.valueMicros);
}

function selectedMicros(flow: Pick<HaloFlow, 'ticketsMicros' | 'valueMicros'>, metric: PerfHaloAnalysisMetric): number {
  return metric === 'tickets' ? flow.ticketsMicros : flow.valueMicros;
}

function productionIdentity(
  id: string | null | undefined,
  name: string | null | undefined,
  fallback: string,
): ProductionIdentity {
  const resolvedId = id?.trim() || fallback;
  return {
    id: resolvedId,
    name: name?.trim() || (id?.trim() ? id : 'Unknown production'),
  };
}

export function perfHaloFlows(response: ReportQueryResponse | undefined): HaloFlow[] {
  const grouped = new Map<string, HaloFlow>();
  for (const row of response?.rows ?? []) {
    const source = productionIdentity(
      row.dimensions.assigned_production_id,
      row.dimensions.assigned_production_name,
      '__unknown_source__',
    );
    const destination = productionIdentity(
      row.dimensions.sold_production_id,
      row.dimensions.sold_production_name,
      '__unknown_destination__',
    );
    const audienceType = row.dimensions.audience_type || 'Unknown';
    const kind = source.id === destination.id ? 'direct' : 'halo';
    const key = `${source.id}\u0000${destination.id}\u0000${audienceType}\u0000${kind}`;
    const current = grouped.get(key) ?? {
      key,
      source,
      destination,
      audienceType,
      kind,
      ticketsMicros: 0,
      valueMicros: 0,
    };
    current.ticketsMicros += finite(row.metrics.attributed_conversions_micros);
    current.valueMicros += finite(row.metrics.attributed_revenue_micros);
    grouped.set(key, current);
  }
  return [...grouped.values()].sort((left, right) => left.key.localeCompare(right.key));
}

function collapseAudience(flows: readonly HaloFlow[]): SankeyFlow[] {
  const grouped = new Map<string, SankeyFlow>();
  for (const flow of flows) {
    const key = `${flow.source.id}\u0000${flow.destination.id}\u0000${flow.kind}`;
    const current = grouped.get(key) ?? {
      ...flow,
      key,
      sourceCount: 1,
      destinationCount: 1,
    };
    current.ticketsMicros += grouped.has(key) ? flow.ticketsMicros : 0;
    current.valueMicros += grouped.has(key) ? flow.valueMicros : 0;
    grouped.set(key, current);
  }
  return [...grouped.values()];
}

function rankedVisibleIds(
  flows: readonly SankeyFlow[],
  side: 'source' | 'destination',
  metric: PerfHaloAnalysisMetric,
): { visible: Set<string>; omitted: Set<string> } {
  const totals = new Map<string, { name: string; value: number }>();
  for (const flow of flows) {
    const identity = flow[side];
    const current = totals.get(identity.id) ?? { name: identity.name, value: 0 };
    current.value += Math.abs(selectedMicros(flow, metric));
    totals.set(identity.id, current);
  }
  const ranked = [...totals.entries()].sort((left, right) => (
    right[1].value - left[1].value
      || left[1].name.localeCompare(right[1].name)
      || left[0].localeCompare(right[0])
  ));
  const keep = ranked.length > MAX_VISIBLE_NODES
    ? MAX_VISIBLE_NODES - 1
    : MAX_VISIBLE_NODES;
  const visible = new Set(ranked.slice(0, keep).map(([id]) => id));
  const omitted = new Set(ranked.slice(keep).map(([id]) => id));
  return { visible, omitted };
}

export function collapseSankeyFlows(
  input: readonly HaloFlow[],
  metric: PerfHaloAnalysisMetric,
): {
  flows: SankeyFlow[];
  omittedSourceCount: number;
  omittedDestinationCount: number;
} {
  const flows = collapseAudience(input).filter(
    (flow) => selectedMicros(flow, metric) !== 0,
  );
  const sourceIds = rankedVisibleIds(flows, 'source', metric);
  const destinationIds = rankedVisibleIds(flows, 'destination', metric);
  const grouped = new Map<string, SankeyFlow>();
  for (const flow of flows) {
    const source = sourceIds.omitted.has(flow.source.id)
      ? { id: OTHER_SOURCE_ID, name: 'Other productions' }
      : flow.source;
    const destination = destinationIds.omitted.has(flow.destination.id)
      ? { id: OTHER_DESTINATION_ID, name: 'Other productions' }
      : flow.destination;
    const key = `${source.id}\u0000${destination.id}\u0000${flow.kind}`;
    const current = grouped.get(key) ?? {
      ...flow,
      key,
      source,
      destination,
      ticketsMicros: 0,
      valueMicros: 0,
      sourceCount: 0,
      destinationCount: 0,
    };
    current.ticketsMicros += flow.ticketsMicros;
    current.valueMicros += flow.valueMicros;
    current.sourceCount += flow.sourceCount;
    current.destinationCount += flow.destinationCount;
    grouped.set(key, current);
  }
  return {
    flows: [...grouped.values()].sort((left, right) => left.key.localeCompare(right.key)),
    omittedSourceCount: sourceIds.omitted.size,
    omittedDestinationCount: destinationIds.omitted.size,
  };
}

function tooltipPosition(rect: DOMRect): Pick<TooltipState, 'x' | 'y'> {
  return {
    x: Math.max(8, Math.min(window.innerWidth - 268, rect.left + rect.width / 2)),
    y: Math.max(72, Math.min(window.innerHeight - 80, rect.top + rect.height / 2)),
  };
}

function pointerTooltipPosition(x: number, y: number): Pick<TooltipState, 'x' | 'y'> {
  return {
    x: Math.max(8, Math.min(window.innerWidth - 268, x + 12)),
    y: Math.max(72, Math.min(window.innerHeight - 80, y + 12)),
  };
}

function flowTooltip(flow: SankeyFlow, x: number, y: number): TooltipState {
  return {
    title: `${flow.source.name} → ${flow.destination.name}`,
    lines: [
      { label: 'Flow', value: flow.kind === 'direct' ? 'Direct' : 'Halo' },
      { label: 'Tickets', value: formatTicketsMicros(flow.ticketsMicros) },
      { label: 'Ticket Value', value: formatValueMicros(flow.valueMicros) },
    ],
    x,
    y,
  };
}

function InteractiveTooltip({ tooltip }: { tooltip: TooltipState | null }) {
  if (!tooltip) return null;
  return (
    <div
      className="perf-halo-tooltip"
      role="tooltip"
      style={{ left: tooltip.x, top: tooltip.y }}
    >
      <strong>{tooltip.title}</strong>
      {tooltip.lines.map((line) => (
        <span key={line.label}><b>{line.label}</b>{line.value}</span>
      ))}
    </div>
  );
}

/**
 * Uses one magnitude scale for both sides, then allocates each ribbon as a
 * proportional share of its own source and destination node. No visual
 * minimum is applied here: small activity must stay small, while the separate
 * transparent interaction path in SankeyDiagram supplies the usable hit area.
 */
export function buildSankeyGeometry(
  flows: readonly SankeyFlow[],
  metric: PerfHaloAnalysisMetric,
  width: number,
  height = SANKEY_HEIGHT,
) {
  const sourceTotals = new Map<string, { identity: ProductionIdentity; value: number }>();
  const destinationTotals = new Map<string, { identity: ProductionIdentity; value: number }>();
  for (const flow of flows) {
    const value = Math.abs(selectedMicros(flow, metric));
    const source = sourceTotals.get(flow.source.id) ?? { identity: flow.source, value: 0 };
    const destination = destinationTotals.get(flow.destination.id) ?? { identity: flow.destination, value: 0 };
    source.value += value;
    destination.value += value;
    sourceTotals.set(flow.source.id, source);
    destinationTotals.set(flow.destination.id, destination);
  }

  const ranked = (totals: typeof sourceTotals) => [...totals.values()].sort((left, right) => (
    right.value - left.value
      || left.identity.name.localeCompare(right.identity.name)
      || left.identity.id.localeCompare(right.identity.id)
  ));
  const sources = ranked(sourceTotals);
  const destinations = ranked(destinationTotals);
  const availableHeight = height - SANKEY_PADDING.top - SANKEY_PADDING.bottom;
  const sourceGapHeight = Math.max(0, sources.length - 1) * SANKEY_NODE_GAP;
  const destinationGapHeight = Math.max(0, destinations.length - 1) * SANKEY_NODE_GAP;
  const nodeAreaHeight = Math.max(
    0,
    availableHeight - Math.max(sourceGapHeight, destinationGapHeight),
  );
  const sourceActivity = sources.reduce((sum, item) => sum + item.value, 0);
  const destinationActivity = destinations.reduce((sum, item) => sum + item.value, 0);
  const totalActivity = Math.max(sourceActivity, destinationActivity);
  const scale = totalActivity > 0 ? nodeAreaHeight / totalActivity : 0;

  const position = (
    values: typeof sources,
    x: number,
  ): Map<string, NodeGeometry> => {
    const occupiedHeight = values.reduce((sum, value) => sum + value.value * scale, 0)
      + Math.max(0, values.length - 1) * SANKEY_NODE_GAP;
    let y = SANKEY_PADDING.top + Math.max(0, (availableHeight - occupiedHeight) / 2);
    const nodes = new Map<string, NodeGeometry>();
    for (const value of values) {
      const nodeHeight = value.value * scale;
      nodes.set(value.identity.id, {
        id: value.identity.id,
        name: value.identity.name,
        x,
        y,
        height: nodeHeight,
        value: value.value,
      });
      y += nodeHeight + SANKEY_NODE_GAP;
    }
    return nodes;
  };

  const sourceX = SANKEY_PADDING.left;
  const destinationX = width - SANKEY_PADDING.right;
  const sourceNodes = position(sources, sourceX);
  const destinationNodes = position(destinations, destinationX);
  const sourceUsed = new Map<string, number>();
  const destinationUsed = new Map<string, number>();
  const sourceFlowsRemaining = new Map<string, number>();
  const destinationFlowsRemaining = new Map<string, number>();
  const orderedFlows = [...flows].sort((left, right) => (
    left.source.id.localeCompare(right.source.id)
      || left.destination.id.localeCompare(right.destination.id)
      || left.kind.localeCompare(right.kind)
  ));
  for (const flow of orderedFlows) {
    sourceFlowsRemaining.set(flow.source.id, (sourceFlowsRemaining.get(flow.source.id) ?? 0) + 1);
    destinationFlowsRemaining.set(
      flow.destination.id,
      (destinationFlowsRemaining.get(flow.destination.id) ?? 0) + 1,
    );
  }

  const allocate = (
    node: NodeGeometry,
    value: number,
    used: Map<string, number>,
    remaining: Map<string, number>,
  ): { offset: number; height: number } => {
    const offset = used.get(node.id) ?? 0;
    const available = Math.max(0, node.height - offset);
    const flowsRemaining = remaining.get(node.id) ?? 1;
    // Giving the last flow the exact remainder prevents floating-point drift
    // from ever placing a ribbon fractionally outside its node.
    const proportional = node.value > 0 ? node.height * value / node.value : 0;
    const ribbonHeight = flowsRemaining <= 1
      ? available
      : Math.min(available, proportional);
    used.set(node.id, offset + ribbonHeight);
    remaining.set(node.id, Math.max(0, flowsRemaining - 1));
    return { offset, height: ribbonHeight };
  };

  const ribbons: RibbonGeometry[] = [];
  for (const flow of orderedFlows) {
    const source = sourceNodes.get(flow.source.id);
    const destination = destinationNodes.get(flow.destination.id);
    if (!source || !destination) continue;
    const value = Math.abs(selectedMicros(flow, metric));
    const sourceAllocation = allocate(source, value, sourceUsed, sourceFlowsRemaining);
    const destinationAllocation = allocate(
      destination,
      value,
      destinationUsed,
      destinationFlowsRemaining,
    );
    const x0 = source.x + SANKEY_NODE_WIDTH;
    const x1 = destination.x;
    const control = (x0 + x1) / 2;
    const y0 = source.y + sourceAllocation.offset;
    const y1 = destination.y + destinationAllocation.offset;
    const path = [
      `M ${x0} ${y0}`,
      `C ${control} ${y0}, ${control} ${y1}, ${x1} ${y1}`,
      `L ${x1} ${y1 + destinationAllocation.height}`,
      `C ${control} ${y1 + destinationAllocation.height}, ${control} ${y0 + sourceAllocation.height}, ${x0} ${y0 + sourceAllocation.height}`,
      'Z',
    ].join(' ');
    ribbons.push({
      flow,
      path,
      sourceOffset: sourceAllocation.offset,
      sourceHeight: sourceAllocation.height,
      destinationOffset: destinationAllocation.offset,
      destinationHeight: destinationAllocation.height,
    });
  }

  return { sourceNodes, destinationNodes, ribbons, sourceX, destinationX };
}

function SankeyDiagram({
  input,
  metric,
}: {
  input: readonly HaloFlow[];
  metric: PerfHaloAnalysisMetric;
}) {
  const [container, containerRef] = useAttachedElement<HTMLDivElement>();
  const [width, setWidth] = useState(920);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const collapsed = useMemo(() => collapseSankeyFlows(input, metric), [input, metric]);

  useEffect(() => {
    if (!container) return;
    const measure = () => setWidth(Math.max(620, container.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [container]);

  const geometry = useMemo(
    () => buildSankeyGeometry(collapsed.flows, metric, width),
    [collapsed.flows, metric, width],
  );

  const pointTooltip = (event: PointerEvent<SVGPathElement>, flow: SankeyFlow) => {
    const position = pointerTooltipPosition(event.clientX, event.clientY);
    setTooltip(flowTooltip(flow, position.x, position.y));
  };
  const focusTooltip = (element: SVGPathElement, flow: SankeyFlow) => {
    const position = tooltipPosition(element.getBoundingClientRect());
    setTooltip(flowTooltip(flow, position.x, position.y));
  };
  const keyDown = (event: KeyboardEvent<SVGPathElement>, flow: SankeyFlow) => {
    if (event.key === 'Escape') {
      setTooltip(null);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      focusTooltip(event.currentTarget, flow);
    }
  };

  if (!collapsed.flows.length) {
    return <div className="perf-halo-empty">No Direct or Halo production flows match these filters.</div>;
  }

  return (
    <div className="perf-halo-sankey" ref={containerRef}>
      <div className="perf-halo-sankey-headings" aria-hidden="true">
        <span>Assigned production</span><span>Purchased production</span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${SANKEY_HEIGHT}`}
        width="100%"
        height={SANKEY_HEIGHT}
        role="group"
        aria-label={`${metric === 'tickets' ? 'Ticket' : 'Ticket Value'} flow from assigned to purchased productions`}
      >
        <g className="perf-halo-ribbons">
          {geometry.ribbons.map(({ flow, path }) => {
            const label = `${flow.source.name} to ${flow.destination.name}, ${flow.kind}, ${formatMetricMicros(flow, metric)}`;
            return (
              <g key={flow.key} className="perf-halo-ribbon-group">
                <path
                  d={path}
                  className={`perf-halo-ribbon perf-halo-ribbon--${flow.kind}`}
                  aria-hidden="true"
                  data-visible-ribbon=""
                />
                <path
                  d={path}
                  className={`perf-halo-ribbon-hit perf-halo-ribbon--${flow.kind}`}
                  role="button"
                  tabIndex={0}
                  aria-label={label}
                  onPointerEnter={(event) => pointTooltip(event, flow)}
                  onPointerMove={(event) => pointTooltip(event, flow)}
                  onPointerLeave={(event) => {
                    if (event.pointerType !== 'touch') setTooltip(null);
                  }}
                  onPointerDown={(event) => pointTooltip(event, flow)}
                  onFocus={(event) => focusTooltip(event.currentTarget, flow)}
                  onBlur={() => setTooltip(null)}
                  onKeyDown={(event) => keyDown(event, flow)}
                />
              </g>
            );
          })}
        </g>
        {[...geometry.sourceNodes.values()].map((node) => (
          <g key={`source-${node.id}`}>
            <rect x={node.x} y={node.y} width={SANKEY_NODE_WIDTH} height={node.height} className="perf-halo-node perf-halo-node--source"/>
            <text x={node.x - 7} y={node.y + node.height / 2} textAnchor="end" dominantBaseline="middle" className="perf-halo-node-label">
              {node.name}
            </text>
          </g>
        ))}
        {[...geometry.destinationNodes.values()].map((node) => (
          <g key={`destination-${node.id}`}>
            <rect x={node.x} y={node.y} width={SANKEY_NODE_WIDTH} height={node.height} className="perf-halo-node perf-halo-node--destination"/>
            <text x={node.x + SANKEY_NODE_WIDTH + 7} y={node.y + node.height / 2} dominantBaseline="middle" className="perf-halo-node-label">
              {node.name}
            </text>
          </g>
        ))}
      </svg>
      {(collapsed.omittedSourceCount > 0 || collapsed.omittedDestinationCount > 0) && (
        <p className="perf-halo-disclosure">
          “Other productions” aggregates {collapsed.omittedSourceCount.toLocaleString()} assigned and {collapsed.omittedDestinationCount.toLocaleString()} purchased productions. All activity remains included.
        </p>
      )}
      <InteractiveTooltip tooltip={tooltip}/>
    </div>
  );
}

function ShowMatrix({
  flows,
  metric,
}: {
  flows: readonly HaloFlow[];
  metric: PerfHaloAnalysisMetric;
}) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const collapsed = useMemo(() => collapseAudience(flows), [flows]);
  const sources = useMemo(() => {
    const values = new Map<string, ProductionIdentity>();
    collapsed.forEach((flow) => values.set(flow.source.id, flow.source));
    return [...values.values()].sort((left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id));
  }, [collapsed]);
  const destinations = useMemo(() => {
    const values = new Map<string, ProductionIdentity>();
    collapsed.forEach((flow) => values.set(flow.destination.id, flow.destination));
    return [...values.values()].sort((left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id));
  }, [collapsed]);
  const byPair = useMemo(() => {
    const values = new Map<string, SankeyFlow>();
    for (const flow of collapsed) {
      const key = `${flow.source.id}\u0000${flow.destination.id}`;
      const current = values.get(key) ?? { ...flow, key, ticketsMicros: 0, valueMicros: 0 };
      current.ticketsMicros += flow.ticketsMicros;
      current.valueMicros += flow.valueMicros;
      values.set(key, current);
    }
    return values;
  }, [collapsed]);
  const maxMagnitude = Math.max(1, ...[...byPair.values()].map((flow) => Math.abs(selectedMicros(flow, metric))));

  const showTooltip = (flow: SankeyFlow, rect: DOMRect, x?: number, y?: number) => {
    const position = x == null || y == null ? tooltipPosition(rect) : { x, y };
    setTooltip(flowTooltip(flow, position.x, position.y));
  };

  if (!sources.length || !destinations.length) return <div className="perf-halo-empty">No show-to-show flows match these filters.</div>;
  return (
    <div className="perf-halo-matrix-scroll" tabIndex={0} aria-label="Show-to-show Direct and Halo matrix">
      <table className="perf-halo-matrix">
        <thead>
          <tr>
            <th className="perf-halo-matrix-corner">Assigned ↓ / Purchased →</th>
            {destinations.map((destination) => <th key={destination.id} title={destination.name}>{destination.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {sources.map((source) => (
            <tr key={source.id}>
              <th title={source.name}>{source.name}</th>
              {destinations.map((destination) => {
                const flow = byPair.get(`${source.id}\u0000${destination.id}`);
                const value = flow ? selectedMicros(flow, metric) : 0;
                const magnitude = Math.abs(value) / maxMagnitude;
                const kind = source.id === destination.id ? 'direct' : 'halo';
                const fallback: SankeyFlow = {
                  key: `${source.id}-${destination.id}`,
                  source,
                  destination,
                  audienceType: 'All',
                  kind,
                  ticketsMicros: 0,
                  valueMicros: 0,
                  sourceCount: 1,
                  destinationCount: 1,
                };
                const item = flow ?? fallback;
                const aria = `${source.name} to ${destination.name}, ${kind}, ${formatMetricMicros(item, metric)}`;
                return (
                  <td
                    key={destination.id}
                    className={`perf-halo-matrix-cell perf-halo-matrix-cell--${kind}`}
                    style={{
                      '--halo-intensity': value === 0
                        ? '0%'
                        : `${matrixCellIntensity(kind, magnitude)}%`,
                    } as CSSProperties}
                  >
                    <button
                      type="button"
                      aria-label={aria}
                      onPointerEnter={(event) => {
                        const position = pointerTooltipPosition(event.clientX, event.clientY);
                        showTooltip(item, event.currentTarget.getBoundingClientRect(), position.x, position.y);
                      }}
                      onPointerMove={(event) => {
                        const position = pointerTooltipPosition(event.clientX, event.clientY);
                        showTooltip(item, event.currentTarget.getBoundingClientRect(), position.x, position.y);
                      }}
                      onPointerLeave={(event) => {
                        if (event.pointerType !== 'touch') setTooltip(null);
                      }}
                      onPointerDown={(event) => {
                        const position = pointerTooltipPosition(event.clientX, event.clientY);
                        showTooltip(item, event.currentTarget.getBoundingClientRect(), position.x, position.y);
                      }}
                      onFocus={(event) => showTooltip(item, event.currentTarget.getBoundingClientRect())}
                      onBlur={() => setTooltip(null)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') setTooltip(null);
                      }}
                    >
                      {formatMetricMicros(item, metric)}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <InteractiveTooltip tooltip={tooltip}/>
    </div>
  );
}

function audienceSummaries(
  flows: readonly HaloFlow[],
  attributionResponse: ReportQueryResponse | undefined,
  performance: ReportQueryResponse | undefined,
): AudienceSummary[] {
  const attribution = new Map<string, { directTickets: number; haloTickets: number; directValue: number; haloValue: number }>();
  for (const flow of flows) {
    const current = attribution.get(flow.audienceType) ?? { directTickets: 0, haloTickets: 0, directValue: 0, haloValue: 0 };
    if (flow.kind === 'direct') {
      current.directTickets += flow.ticketsMicros;
      current.directValue += flow.valueMicros;
    } else {
      current.haloTickets += flow.ticketsMicros;
      current.haloValue += flow.valueMicros;
    }
    attribution.set(flow.audienceType, current);
  }
  const spend = new Map<string, number>();
  const attributionSpend = new Map<string, number | null>();
  for (const row of performance?.rows ?? []) {
    const type = row.dimensions.audience_type || 'Unknown';
    const metrics = projectPerfHaloMetrics(row.metrics);
    spend.set(type, (spend.get(type) ?? 0) + metrics.gross_spend_cents);
    const currentAttributionSpend = attributionSpend.get(type);
    attributionSpend.set(
      type,
      currentAttributionSpend === null || metrics.attribution_gross_spend_cents === null
        ? null
        : (currentAttributionSpend ?? 0) + metrics.attribution_gross_spend_cents,
    );
  }
  const types = new Set([...attribution.keys(), ...spend.keys()]);
  const attributionAvailable = reportMetricIsAvailable(
    reportingMetricAvailability('attribution', attributionResponse?.freshness),
    'attribution',
  );
  const deliveryAvailable = reportMetricIsAvailable(
    reportingMetricAvailability('performance', performance?.freshness),
    'delivery',
  );
  return [...types].map((type) => {
    const values = attribution.get(type) ?? {
      directTickets: 0,
      haloTickets: 0,
      directValue: 0,
      haloValue: 0,
    };
    return {
      type,
      attributionAvailable,
      deliveryAvailable,
      metrics: {
        gross_spend_cents: spend.get(type) ?? 0,
        attribution_gross_spend_cents: attributionSpend.get(type) ?? null,
        impressions: 0,
        clicks: 0,
        total_tickets_micros: values.directTickets + values.haloTickets,
        direct_tickets_micros: values.directTickets,
        halo_tickets_micros: values.haloTickets,
        total_ticket_value_micros: values.directValue + values.haloValue,
        direct_ticket_value_micros: values.directValue,
        halo_ticket_value_micros: values.haloValue,
      },
    };
  }).sort((left, right) => (
    right.metrics.total_ticket_value_micros - left.metrics.total_ticket_value_micros
      || left.type.localeCompare(right.type)
  ));
}

function breakdownLine(
  metrics: PerfHaloMetrics,
  metric: 'attributed_conversions' | 'attributed_revenue' | 'roas',
  available = true,
): string {
  if (!available) return 'Direct — · Halo —';
  const direct = perfHaloMetricValue(metrics, metric, 'direct');
  const halo = perfHaloMetricValue(metrics, metric, 'halo');
  const format = metric === 'attributed_conversions'
    ? (value: number | null) => value == null ? '—' : value.toLocaleString('en-US', { maximumFractionDigits: 6 })
    : metric === 'attributed_revenue'
      ? (value: number | null) => value == null ? '—' : value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      : (value: number | null) => value == null ? '—' : `${value.toFixed(2)}×`;
  return `Direct ${format(direct)} · Halo ${format(halo)}`;
}

function HaloAudienceTypes({ summaries }: { summaries: readonly AudienceSummary[] }) {
  if (!summaries.length) return <div className="perf-halo-empty">No audience-type attribution matches these filters.</div>;
  return (
    <div className="perf-halo-audience-list">
      {summaries.map(({ type, metrics, attributionAvailable, deliveryAvailable }) => {
        const roasAvailable = attributionAvailable
          && deliveryAvailable
          && metrics.attribution_gross_spend_cents !== null;
        const tickets = attributionAvailable
          ? perfHaloMetricValue(metrics, 'attributed_conversions')
          : null;
        const value = attributionAvailable
          ? perfHaloMetricValue(metrics, 'attributed_revenue')
          : null;
        const roas = roasAvailable ? perfHaloMetricValue(metrics, 'roas') : null;
        const haloValue = finite(metrics.halo_ticket_value_micros);
        const totalMagnitude = Math.abs(finite(metrics.direct_ticket_value_micros)) + Math.abs(haloValue);
        const haloShare = attributionAvailable && totalMagnitude > 0
          ? Math.abs(haloValue) / totalMagnitude * 100
          : null;
        return (
          <article key={type} className="perf-halo-audience-row">
            <div className="perf-halo-audience-name">{type}</div>
            <div><span>Tickets</span><strong>{tickets?.toLocaleString('en-US', { maximumFractionDigits: 6 }) ?? '—'}</strong><small>{breakdownLine(metrics, 'attributed_conversions', attributionAvailable)}</small></div>
            <div><span>Ticket Value</span><strong>{value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '—'}</strong><small>{breakdownLine(metrics, 'attributed_revenue', attributionAvailable)}</small></div>
            <div><span>ROAS</span><strong>{roas == null ? '—' : `${roas.toFixed(2)}×`}</strong><small>{breakdownLine(metrics, 'roas', roasAvailable)}</small></div>
            <div><span>Halo share</span><strong>{haloShare == null ? '—' : `${haloShare.toFixed(1)}%`}</strong><small>Absolute Ticket Value activity</small></div>
          </article>
        );
      })}
    </div>
  );
}

function TopHaloProductions({
  flows,
  metric,
}: {
  flows: readonly HaloFlow[];
  metric: PerfHaloAnalysisMetric;
}) {
  const rows = useMemo(() => {
    const grouped = new Map<string, {
      destination: ProductionIdentity;
      sources: Set<string>;
      ticketsMicros: number;
      valueMicros: number;
    }>();
    for (const flow of flows) {
      if (flow.kind !== 'halo') continue;
      const current = grouped.get(flow.destination.id) ?? {
        destination: flow.destination,
        sources: new Set<string>(),
        ticketsMicros: 0,
        valueMicros: 0,
      };
      current.sources.add(flow.source.id);
      current.ticketsMicros += flow.ticketsMicros;
      current.valueMicros += flow.valueMicros;
      grouped.set(flow.destination.id, current);
    }
    return [...grouped.values()].sort((left, right) => (
      selectedMicros(right, metric) - selectedMicros(left, metric)
        || left.destination.name.localeCompare(right.destination.name)
        || left.destination.id.localeCompare(right.destination.id)
    ));
  }, [flows, metric]);
  if (!rows.length) return <div className="perf-halo-empty">No off-diagonal Halo activity matches these filters.</div>;
  return (
    <div className="perf-halo-ranking">
      {rows.map((row, index) => (
        <div key={row.destination.id} className="perf-halo-ranking-row">
          <span className="perf-halo-rank">{index + 1}</span>
          <strong>{row.destination.name}</strong>
          <span>{row.sources.size.toLocaleString()} source {row.sources.size === 1 ? 'production' : 'productions'}</span>
          <b>{formatMetricMicros(row, metric)}</b>
          <small>{formatTicketsMicros(row.ticketsMicros)} Tickets · {formatValueMicros(row.valueMicros)} Ticket Value</small>
        </div>
      ))}
    </div>
  );
}

export function PerfHaloAnalysis({
  flowResponse,
  audiencePerformanceResponse,
  metric,
  onMetricChange,
  loading = false,
}: {
  flowResponse: ReportQueryResponse | undefined;
  audiencePerformanceResponse: ReportQueryResponse | undefined;
  metric: PerfHaloAnalysisMetric;
  onMetricChange: (metric: PerfHaloAnalysisMetric) => void;
  loading?: boolean;
}) {
  const flows = useMemo(() => perfHaloFlows(flowResponse), [flowResponse]);
  const summaries = useMemo(
    () => audienceSummaries(flows, flowResponse, audiencePerformanceResponse),
    [audiencePerformanceResponse, flowResponse, flows],
  );
  if (loading && !flowResponse) return <div className="perf-halo-empty">Loading Halo analysis…</div>;
  return (
    <div className="perf-halo-analysis">
      <section className="perf-halo-panel" aria-labelledby="halo-flow-title">
        <div className="perf-halo-panel-heading">
          <div>
            <h2 id="halo-flow-title">Production flow</h2>
            <p>Direct appears on the diagonal; Halo connects assigned productions to other purchased productions.</p>
          </div>
          <div className="perf-halo-metric-select">
            <span>Metric</span>
            <Dropdown
              label=""
              value={metric}
              options={HALO_METRIC_OPTIONS}
              onChange={(value: string) => onMetricChange(value as PerfHaloAnalysisMetric)}
              ariaLabel="Halo analysis metric"
              width={160}
              rightAlign
            />
          </div>
        </div>
        <SankeyDiagram input={flows} metric={metric}/>
      </section>

      <section className="perf-halo-panel" aria-labelledby="halo-audience-title">
        <div className="perf-halo-panel-heading">
          <div>
            <h2 id="halo-audience-title">Halo by audience type</h2>
            <p>Total-first attribution with Direct and Halo contributions.</p>
          </div>
        </div>
        <HaloAudienceTypes summaries={summaries}/>
      </section>

      <section className="perf-halo-panel" aria-labelledby="halo-matrix-title">
        <div className="perf-halo-panel-heading">
          <div>
            <h2 id="halo-matrix-title">Show-to-show matrix</h2>
            <p>Direct diagonal and off-diagonal Halo flows; intensity uses absolute magnitude.</p>
          </div>
        </div>
        <ShowMatrix flows={flows} metric={metric}/>
      </section>

      <section className="perf-halo-panel" aria-labelledby="halo-ranking-title">
        <div className="perf-halo-panel-heading">
          <div>
            <h2 id="halo-ranking-title">Top halo productions</h2>
            <p>Purchased productions ranked by off-diagonal Halo activity.</p>
          </div>
        </div>
        <TopHaloProductions flows={flows} metric={metric}/>
      </section>
    </div>
  );
}
